import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Import components
import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionContact from "../components/common/DivisionContact";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import Activities from "../components/HamdanTyres/Activities";
import Branches from "../components/HamdanTyres/Branches";

import { Building2, Target, MapPin, Phone, Users, Wrench } from "lucide-react";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";

const HamdanTyres: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setContent(data.Tyres || {});
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Get current tab from URL, fallback "about"
  const activeTab = searchParams.get("tab") || "about";

  const menuItems = [
    { id: "about", label: t('common.about'), icon: <Building2 className="w-5 h-5" /> },
    { id: "services", label: t('common.services'), icon: <Wrench className="w-5 h-5" /> },
    { id: "promotions", label: t('common.promotions'), icon: <Target className="w-5 h-5" /> },
    { id: "locations", label: t('common.branches'), icon: <MapPin className="w-5 h-5" /> },
    { id: "clients", label: t('common.clients'), icon: <Users className="w-5 h-5" /> },
    { id: "contact", label: t('common.contact'), icon: <Phone className="w-5 h-5" /> },
  ];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-red-50 border-t-red-600 rounded-full animate-spin mb-6" />
      <p className="text-gray-500 font-medium">{t('common.loading')}</p>
    </div>
  );

  const T = content || {};
  const clientLogoImages = buildClientLogoImages(T);
  const clientLogoUrls = t_api(T.clients_logo_urls, T.clients_logo_urls_ar);

  return (
    <GroupPageShell
      heroContent={{
        title: t_api(T.banner_title, T.banner_title_ar),
        subtitle: t_api(T.banner_subtitle, T.banner_subtitle_ar),
        banners: [
          { image: T.banner_1 || '/banner/hamdan-tyres-1.jpg' },
          { image: T.banner_2 || '/banner/hamdan-tyres-2.jpg' },
          { image: T.banner_3 || '/banner/hamdan-tyres-3.jpg' },
        ]
      }}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8">
        {activeTab === "about" && (
          <>
            <DivisionIntro content={{
              kicker: "Hamdan Group - Oman",
              title: t_api(T.about_title, T.about_title_ar),
              desc: t_api(T.about_desc, T.about_desc_ar),
              image: T.about_image || "/Hamdan-Dunlop-logo.jpg",
              imageFit: "contain",
              imageBadgeTitle: t_api("Tyres & Accessories", "الإطارات والملحقات"),
              imageLabel: t_api(T.about_title, T.about_title_ar) || t_api("Hamdan Tyres", "إطارات حمدان"),
              noteTitle: t_api("Distribution", "التوزيع"),
              noteDesc: t_api(T.network_desc, T.network_desc_ar) || t_api("Registered sole distributor of Dunlop Tyres and Tubes in Oman since 1981.", "الموزع الوحيد المسجل لإطارات وأنابيب دنلوب في عمان منذ عام 1981."),
              panelTitle: t_api("INTRODUCTION", "مقدمة"),
              capabilitiesTitle: t_api("Tyres Capabilities", "إمكانيات الإطارات"),
              capabilitiesMeta: "Dunlop - Branches - Oman",
              brandLogo: "/Hamdan-Dunlop-logo.jpg",
              brandDesc: t_api("Supplying tyres, batteries and accessories through a growing branch network across Oman.", "توريد الإطارات والبطاريات والملحقات من خلال شبكة فروع متنامية في جميع أنحاء عمان."),
              brandTags: [t_api("Tyres", "إطارات"), t_api("Dunlop", "دنلوب"), t_api("Accessories", "ملحقات")],
              points: [1, 2, 3].map(i => ({
                title: t_api(T[`about_pt_${i}_title`], T[`about_pt_${i}_title_ar`]),
                desc: t_api(T[`about_pt_${i}_desc`], T[`about_pt_${i}_desc_ar`]),
                icon: T[`about_pt_${i}_icon`] || ['Globe2', 'Award', 'MapPin'][i - 1],
              })).filter(p => p.title)
            }} />
            <DivisionClientsStrip
              title={t_api(T.clients_title, T.clients_title_ar)}
              desc={t_api(T.clients_desc, T.clients_desc_ar)}
              clientsText={t_api(T.clients_list, T.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={content} />
        )}
        {activeTab === "promotions" && (
          <Activities content={{
            promo_date: t_api(T.promo_date, T.promo_date_ar),
            promo_year: t_api(T.promo_year, T.promo_year_ar),
            promo_title: t_api(T.promo_title, T.promo_title_ar),
            promo_subtitle: t_api(T.promo_subtitle, T.promo_subtitle_ar),
            promo_image_1: T.promo_image_1,
            promo_image_2: T.promo_image_2,
            points: [1, 2, 3].map(i => ({
              title: t_api(T[`promo_pt_${i}_title`], T[`promo_pt_${i}_title_ar`]),
              desc: t_api(T[`promo_pt_${i}_desc`], T[`promo_pt_${i}_desc_ar`]),
            })).filter(p => p.title)
          }} />
        )}
        {activeTab === "locations" && (
          <Branches content={{
            network_title: t_api(T.network_title, T.network_title_ar),
            network_desc: t_api(T.network_desc, T.network_desc_ar),
            stats: [1, 2, 3, 4].map(i => ({
              value: t_api(T[`network_stat_${i}_value`], T[`network_stat_${i}_value_ar`]),
              label: t_api(T[`network_stat_${i}_label`], T[`network_stat_${i}_label_ar`]),
            })),
            branches: Array.from({ length: 30 }, (_, idx) => {
              const n = idx + 1;
              return {
                name: t_api(T[`branch_${n}_name`], T[`branch_${n}_name_ar`]),
                phone: T[`branch_${n}_phone`],
                fax: T[`branch_${n}_fax`],
                email: T[`branch_${n}_email`],
                coordinates: T[`branch_${n}_coords`],
              };
            }).filter(b => b.name)
          }} />
        )}
        {activeTab === "clients" && (
          <DivisionClients content={{
            title: t_api(T.clients_title, T.clients_title_ar),
            desc: t_api(T.clients_desc, T.clients_desc_ar),
            clientsText: t_api(T.clients_list, T.clients_list_ar),
            logoUrls: clientLogoUrls,
            logoImages: clientLogoImages,
            divisionLabel: "Hamdan Tyres, Battery & Accessories",
            footer: t_api(T.clients_footer, T.clients_footer_ar),
            footerDesc: t_api(T.clients_footer_desc, T.clients_footer_desc_ar),
          }} />
        )}
        {activeTab === "contact" && (
          <DivisionContact content={{
            title: t_api(T.contact_form_title, T.contact_form_title_ar),
            infoTitle: t_api(T.contact_info_title, T.contact_info_title_ar),
            infoDesc: t_api(T.contact_info_desc, T.contact_info_desc_ar) || "We appreciate your interest in giving to Hamdan Tyres & Services, and we want to make it as easy as possible for you to get your queries answered.",
            contactInfo: [
              {
                location: "Contact Info",
                address: t_api(T.hq_address, T.hq_address_ar) || "Musqat Ghala HQ",
                mobile: T.hq_mobile,
                phone: T.hq_phone,
                fax: T.hq_fax,
                email: T.hq_email,
              },
              {
                location: "Muscat (Al Wadi Al Kabir)",
                phone: T.wadi_phone,
                fax: T.wadi_fax,
                email: T.wadi_email,
              },
            ],
          }} />
        )}
      </div>
    </GroupPageShell>
  );
};

export default HamdanTyres;
